import { Injectable, inject, signal, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApi, LoginResponse } from './auth-api';
import {
  catchError,
  finalize,
  of,
  tap,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApi = inject(AuthApi);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone); 
  private readonly isBrowser = typeof window !== 'undefined';

  readonly isLoggedIn = signal<boolean>(false); 
  private refreshTimeoutId: any = null;
  private refreshRequest$: Observable<LoginResponse> | null = null;
  private readonly logout$ = new Subject<void>();

  /**
   * ==========================================
   * 1. SCENARIO: Refresh Page Verification
   * ==========================================
   * الفحص التلقائي الذي يطلقه الـ APP_INITIALIZER فوراً عند إعادة تحميل الصفحة
   */
  processRefreshVerification(): Observable<any> {
    if (!this.isBrowser) return of(null);

    const accessToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('accessToken') : null;
    const refreshToken = this.getRefreshToken();

    // أ- إذا كان الـ accessToken لا يزال موجوداً وصالحاً بالـ sessionStorage
    if (accessToken) {
      this.isLoggedIn.set(true);
      this.queueSilentRefresh(); // بدء جدولة التايمر للتوكن الحالي لضمان استمرار الجلسة
      return of(true);
    }

    // ب- إذا كان الـ accessToken محذوفاً أو فارغاً (Removed)
    if (!accessToken) {
      
      // نتحقق إذا كان الـ refreshToken موجوداً بالتخزين
      if (refreshToken) {
        console.log('[Refresh Page] الـ accessToken محذوف.. جاري استدعاء الـ refresh-token endpoint وتحديث الستوريج...');
        
        return this.refreshToken().pipe(catchError(() => of(null)));
      } 
      
      // [NO] إذا كان الـ refreshToken غير موجود أيضاً ⬅️ المستخدم غير مسجل دخول
      else {
        console.warn('[Refresh Page] الأكسس توكن والريفرش توكن غير موجودين بالستوريج.');
        this.executeAlternativeScenario();
        return of(null);
      }
    }

    return of(null);
  }

  /**
   * ==========================================
   * 2. SCENARIO: Post-Login Process (Store)
   * ==========================================
   * دالة تخزين البيانات وتوزيعها الحصري على الـ Storages كما طلبت تماماً
   */
  saveLogin(accessToken: string, user: any, rememberMe: boolean, refreshToken?: string, expiresAtUtc?: string) {
    if (!this.isBrowser) return;

    // -> [Post-Login Store]: accessToken (sessionStorage)
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('accessToken', accessToken);
      
      // -> [Post-Login Store]: user info (sessionStorage)
      sessionStorage.setItem('user', JSON.stringify(user));
      
      // حفظ تاريخ انتهاء الصلاحية بصيغة UTC لحساب وقت التايمر بدقة
      if (expiresAtUtc) {
        sessionStorage.setItem('accessTokenExpiresAt', expiresAtUtc);
      }
    }

    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    if (refreshToken) {
      (rememberMe ? localStorage : sessionStorage).setItem('refreshToken', refreshToken);
    }

    // تفعيل حالة الحساب بالتطبيق
    this.isLoggedIn.set(true);

    // جدولة ميعاد التحديث التلقائي القادم للتوكن الجديد (قبل انتهائه بدقيقتين)
    this.queueSilentRefresh();
  }

  /**
   * ==========================================
   * 3. SCENARIO: Timeout Trigger (Browsing Long Time)
   * ==========================================
   * جدولة استدعاء الـ refresh-token التلقائي بالخلفية قبل انتهاء الصلاحية بـ دقيقتين
   */
  private queueSilentRefresh() {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return;

    // تنظيف أي تايمر قديم نشط لمنع التداخل العشوائي والتكرار بالخلفية
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const expiresAtStr = sessionStorage.getItem('accessTokenExpiresAt');
    const refreshToken = this.getRefreshToken();

    if (!expiresAtStr || !refreshToken) return;

    // تحويل وقت انتهاء التوكن (السيرفر) إلى مسافة زمنية بالملي ثانية
    const expiryTime = new Date(expiresAtStr).getTime();
    
    // حساب الوقت الحالي لجهاز المستخدم بناءً على الـ UTC الموحد لتتطابق الحسبة 100% مع وقت السيرفر
    const now = new Date();
    const currentUtcTime = Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
      now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds()
    );

    const timeRemaining = expiryTime - currentUtcTime;
    const bufferTime = 2 * 60 * 1000; // دقيقتين بالملي ثانية (120,000ms)
    const delay = timeRemaining - bufferTime;

    console.log(`[Silent Refresh] المتبقي لصلاحية التوكن الحالي: ${Math.round(timeRemaining / 1000 / 60)} دقيقة.`);
    console.log(`[Silent Refresh] سيتم تحديث الجلسة تلقائياً بالخلفية بعد: ${Math.max(0, Math.round(delay / 1000 / 60))} دقيقة.`);

    if (delay <= 0) {
      // إذا كان المتبقي للتوكن أقل من دقيقتين بالفعل، نحدث فوراً دون انتظار
      this.triggerSilentRefreshRequest();
    } else {
      // تشغيل المؤقت خارج زون أنجولار لضمان أن لا يقوم المتصفح بتجميده بالخلفية أثناء تصفح المستخدم للموقع
      this.zone.runOutsideAngular(() => {
        this.refreshTimeoutId = setTimeout(() => {
          this.zone.run(() => {
            this.triggerSilentRefreshRequest();
          });
        }, delay);
      });
    }
  }

  /**
   * إطلاق طلب الـ Network الفعلي للـ refresh-token وتكرار الـ Post-Login Store
   */
  private triggerSilentRefreshRequest() {
    console.log('[Silent Refresh Network] جاري إرسال طلب تجديد التوكن الصامت إلى الـ endpoint بالنتوورك...');
    
    this.refreshToken().subscribe({ error: () => undefined });
  }

  refreshToken(): Observable<LoginResponse> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout(true);
      return throwError(() => new Error('No refresh token'));
    }

    const rememberMe = localStorage.getItem('refreshToken') !== null;
    this.refreshRequest$ = this.authApi.refreshToken({ refreshToken }).pipe(
      takeUntil(this.logout$),
      tap((response) =>
        this.saveLogin(
          response.accessToken,
          response.user,
          rememberMe,
          response.refreshToken,
          response.accessTokenExpiresAtUtc,
        ),
      ),
      catchError((error) => {
        this.logout(true);
        return throwError(() => error);
      }),
      finalize(() => (this.refreshRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshRequest$;
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken') ?? sessionStorage.getItem('refreshToken');
  }

  /**
   * السيناريو البديل: يتم تشغيله عندما يكون المستخدم غير مسجل دخول أو منتهي التوكنات تماماً
   */
  private executeAlternativeScenario() {
    this.logout();
    console.warn('⚠️ تم تطبيق السيناريو البديل (User is not logged in).');
  }

  /**
   * جلب بيانات المستخدم الحالية المخزنة كـ Object
   */
  getUser() {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * ==========================================
   * 4. SCENARIO: On Logout
   * ==========================================
   * تنظيف كلي لجميع أنواع الـ Storages والـ Timers لمنع أي ثغرات أو استدعاءات عشوائية
   */
  logout(redirectToLogin = false) {
    this.logout$.next();

    if (!this.isBrowser) return;

    // إيقاف التايمر النشط فوراً عند الخروج لحماية موارد المتصفح والـ Network
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    // تنظيف الـ sessionStorage بالكامل
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('accessTokenExpiresAt');
      sessionStorage.removeItem('refreshToken');
    }

    // تنظيف الـ localStorage بالكامل
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('refreshToken');
    }

    this.isLoggedIn.set(false);

    if (redirectToLogin) {
      void this.router.navigate(['/auth/login']);
    }
  }
}
